import { useState, useRef, useEffect } from 'react';
import { useEditMode } from '../contexts/EditModeContext';
import { lumi } from '../lib/lumi';
import { Pencil, Check, X, Loader2, Italic, Underline, Eraser, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTextStyles, ensureFontLoaded } from '../contexts/TextStyleContext';
import BlockStyleBar, { barSelectClass } from './BlockStyleBar';
import { FONT_OPTIONS, FONT_WEIGHT_OPTIONS, getFontById } from '../utils/fonts';
import {
  cleanTextStyle,
  sameTextStyle,
  responsiveSize,
  textStyleToProps,
  TextStyle,
  TEXT_SIZE_OPTIONS } from
'../utils/textStyles';
import {
  applyInlineStyle,
  clearInlineStyle,
  toggleInlineStyle,
  escapeToHtml,
  hasFormatting,
  htmlToPlain,
  sanitizeRichHtml,
  InlineCss } from
'../utils/richText';

interface EditableTextProps {
  /** Collection name in lumi.entities */
  collection: string;
  /** Document _id */
  documentId: string;
  /** Field path to update — supports nested like "content.title" */
  field: string;
  /** Current value to display */
  children: React.ReactNode;
  /** The raw text/html value for editing */
  value?: string;
  /** Type of editor */
  type?: 'text' | 'textarea' | 'html';
  /** CSS class for the wrapper in view mode */
  className?: string;
  /** Inline style for the wrapper in view mode */
  style?: React.CSSProperties;
  /** Callback after successful save — receives new value and field path */
  onSave?: (newValue: string, field?: string) => void;
  /** Render as this HTML tag in view mode */
  as?: keyof JSX.IntrinsicElements;
  /** Additional props for the wrapper element */
  wrapperProps?: Record<string, any>;
}

const EditableText = ({
  collection,
  documentId,
  field,
  children,
  value = '',
  type = 'text',
  className = '',
  style,
  onSave,
  as: Tag = 'div',
  wrapperProps = {}
}: EditableTextProps) => {
  const { isEditMode } = useEditMode();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  // Keep a local copy of the last saved value so we don't depend solely on parent re-rendering
  const [lastSavedValue, setLastSavedValue] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Style de texte (taille, police, graisse, italique) enregistre pour ce champ precis
  const textStyles = useTextStyles();
  const styleKey = `${collection}:${documentId}:${field}`;
  const savedStyle = textStyles ? textStyles.getStyle(styleKey) : {};
  const [editStyle, setEditStyle] = useState<TextStyle>({});
  const { attrs: styleAttrs, css: styleCss } = textStyleToProps(savedStyle);

  // Texte mis en forme caractere par caractere (stocke a part, le texte brut reste la reference)
  const richEnabled = type !== 'html';
  const editorRef = useRef<HTMLDivElement>(null);
  const savedRange = useRef<Range | null>(null);
  const savedRichHtml = textStyles ? textStyles.getRich(styleKey) : '';
  const richActive =
  richEnabled &&
  !!savedRichHtml &&
  htmlToPlain(savedRichHtml) === (lastSavedValue || '').replace(/\n+$/, '');
  const displayHtml = richActive ? sanitizeRichHtml(savedRichHtml, { responsive: true, important: true }) : '';
  const content = richActive ? <span dangerouslySetInnerHTML={{ __html: displayHtml }} /> : children;

  // Sync with prop when parent updates value (e.g. after refetch)
  useEffect(() => {
    setEditValue(value);
    setLastSavedValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing) {
      setEditStyle(textStyles ? textStyles.getStyle(styleKey) : {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  // Contenu initial de l'editeur riche + suivi de la selection de l'utilisateur
  useEffect(() => {
    if (!isEditing || !richEnabled) return;
    const editor = editorRef.current;
    if (!editor) return;
    editor.innerHTML = richActive ? sanitizeRichHtml(savedRichHtml) : escapeToHtml(lastSavedValue || '');
    editor.focus();
    const selection = window.getSelection();
    if (selection) {
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    const onSelectionChange = () => {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && editor.contains(sel.anchorNode)) {
        savedRange.current = sel.getRangeAt(0).cloneRange();
      }
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLTextAreaElement) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
      }
    }
  }, [isEditing]);

  // Not in edit mode or no document — just render children
  if (!isEditMode || !documentId) {
    return (
      <Tag className={className} style={{ ...style, ...styleCss }} {...styleAttrs} {...wrapperProps}>
        {content}
      </Tag>);

  }

  /** Fetch the full current document using get() when available, otherwise fallback to list({filter:{_id}}) */
  const fetchCurrentDoc = async (): Promise<any | null> => {
    const entity = (lumi.entities as any)[collection];
    if (!entity) return null;
    // Try .get(id) first
    if (typeof entity.get === 'function') {
      try {
        const doc = await entity.get(documentId);
        if (doc && (doc._id || doc.id)) return doc;
        // Some SDKs return {data: doc}
        if (doc?.data) return doc.data;
      } catch (err) {
        // fall through to list fallback
        console.warn('[EditableText] get() failed, falling back to list():', err);
      }
    }
    // Fallback: list with filter
    try {
      const res = await entity.list({ filter: { _id: documentId }, limit: 1 });
      return res?.list?.[0] || res?.[0] || null;
    } catch (err) {
      console.error('[EditableText] list() fallback failed:', err);
      return null;
    }
  };

  const handleSave = async () => {
    // Compare against the last saved value, not the (potentially stale) prop value
    let nextText = editValue;
    let nextRich = '';
    if (richEnabled && editorRef.current) {
      const cleanHtml = sanitizeRichHtml(editorRef.current.innerHTML);
      nextText = htmlToPlain(cleanHtml);
      nextRich = hasFormatting(cleanHtml) ? cleanHtml : '';
    }
    const textChanged = nextText !== lastSavedValue;
    const styleChanged = !sameTextStyle(editStyle, savedStyle);
    const richChanged = nextRich !== savedRichHtml;
    if (!textChanged && !styleChanged && !richChanged) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      if ((styleChanged || richChanged) && textStyles) {
        await textStyles.saveEntry(styleKey, editStyle, nextRich);
      }
      if (!textChanged) {
        toast.success('Style enregistre', {
          style: { background: '#2C2C2C', color: '#fff', borderRadius: '8px' },
          duration: 1500
        });
        setIsEditing(false);
        return;
      }

      const parts = field.split('.');
      const updateData: any = {};

      if (parts.length === 1) {
        updateData[field] = nextText;
      } else {
        // Nested field: fetch current doc and merge at the top-level key
        const doc = await fetchCurrentDoc();
        if (!doc) {
          throw new Error(
            `Could not load document "${documentId}" from collection "${collection}" to update nested field "${field}".`
          );
        }
        const topKey = parts[0];
        // Deep-clone the top-level object so we don't mutate the cached doc
        const topValue =
        doc[topKey] && typeof doc[topKey] === 'object' ?
        JSON.parse(JSON.stringify(doc[topKey])) :
        {};
        let cursor: any = topValue;
        for (let i = 1; i < parts.length - 1; i++) {
          if (!cursor[parts[i]] || typeof cursor[parts[i]] !== 'object') {
            cursor[parts[i]] = {};
          }
          cursor = cursor[parts[i]];
        }
        cursor[parts[parts.length - 1]] = nextText;
        updateData[topKey] = topValue;
      }

      updateData.updatedAt = new Date().toISOString();

      // Guard: ensure we're actually sending a meaningful update
      const meaningfulKeys = Object.keys(updateData).filter((k) => k !== 'updatedAt');
      if (meaningfulKeys.length === 0) {
        throw new Error('No field changes to persist (empty update payload).');
      }

      await (lumi.entities as any)[collection].update(documentId, updateData);

      // Update internal baseline so subsequent comparisons work correctly
      setLastSavedValue(nextText);

      toast.success('Saved!', {
        style: { background: '#2C2C2C', color: '#fff', borderRadius: '8px' },
        duration: 1500
      });
      // Notify parent with new value + field so it can refresh its local state
      onSave?.(nextText, field);
      setIsEditing(false);
    } catch (error: any) {
      console.error('[EditableText] Error saving:', error);
      toast.error(error?.message || 'Error saving changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(lastSavedValue);
    setEditStyle(savedStyle);
    setIsEditing(false);
  };

  const updateStyle = (patch: Partial<TextStyle>) => {
    const next = { ...editStyle, ...patch };
    if (patch.fontFamily) ensureFontLoaded(patch.fontFamily);
    setEditStyle(next);
  };

  const previewStyle = (): React.CSSProperties => {
    const s = cleanTextStyle(editStyle);
    const result: React.CSSProperties = { fontFamily: 'inherit', fontSize: 'inherit' };
    if (s.fontSize) result.fontSize = responsiveSize(s.fontSize);
    if (s.fontFamily) result.fontFamily = FONT_OPTIONS.find((f) => f.id === s.fontFamily)?.stack;
    if (s.fontWeight) result.fontWeight = FONT_WEIGHT_OPTIONS.find((w) => w.id === s.fontWeight)?.value;
    if (s.italic) result.fontStyle = 'italic';
    return result;
  };

  const toolbarSelectClass = barSelectClass;

  /** Execute une action sur les caracteres selectionnes dans l'editeur riche */
  const withSelection = (fn: (editor: HTMLElement, range: Range) => Range | null) => {
    const editor = editorRef.current;
    if (!editor) return;
    // On memorise la selection AVANT de redonner le focus a l'editeur
    const stored = savedRange.current ? savedRange.current.cloneRange() : null;
    if (!stored || stored.collapsed || !editor.contains(stored.commonAncestorContainer)) {
      toast("Selectionnez d'abord les lettres ou les mots a modifier", {
        style: { background: '#2C2C2C', color: '#fff', borderRadius: '8px' },
        duration: 2200
      });
      return;
    }
    editor.focus();
    const result = fn(editor, stored);
    if (result) savedRange.current = result;
  };

  const applyCss = (css: InlineCss) => {
    withSelection((editor, range) => applyInlineStyle(editor, range, css));
  };

  const toggleCommand = (command: 'italic' | 'underline') => {
    withSelection((editor, range) => toggleInlineStyle(editor, range, command));
  };

  const clearFormat = () => {
    withSelection((editor, range) => clearInlineStyle(editor, range));
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    }
    if (type === 'text' && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  // Inline tags that cannot contain block elements like <div>
  const inlineTags = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'a', 'label', 'li', 'dt', 'dd', 'td', 'th']);
  const isInlineTag = inlineTags.has(Tag as string);
  // Use a neutral wrapper: span for inline tags, div for block tags
  const EditingWrapper = (isInlineTag ? 'span' : 'div') as React.ElementType;

  // Editing state
  if (isEditing) {
    return (
      <EditingWrapper style={{ position: 'relative', display: isInlineTag ? 'inline-block' : 'block' }} className="group/editing">
        {richEnabled ?
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline={type === 'textarea'}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          className="w-full px-3 py-2 bg-white border-2 border-[#7F1734] rounded-lg shadow-lg text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#7F1734]/30 min-h-[44px] leading-relaxed whitespace-pre-wrap"
          style={{ ...previewStyle(), minWidth: '260px' }} /> :

        type === 'textarea' || type === 'html' ?
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={editValue}
          onChange={(e) => {
            setEditValue(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
          onKeyDown={handleKeyDown}
          className="w-full px-3 py-2 bg-white border-2 border-[#7F1734] rounded-lg shadow-lg text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#7F1734]/30 resize-none min-h-[60px] font-inherit text-inherit leading-relaxed"
          style={previewStyle()} /> :


        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full px-3 py-2 bg-white border-2 border-[#7F1734] rounded-lg shadow-lg text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#7F1734]/30 font-inherit text-inherit"
          style={previewStyle()} />

        }
        {/* Barre riche : style des caracteres selectionnes + style de tout le bloc */}
        {textStyles && richEnabled &&
        <span
          onMouseDown={(e) => {
            const tag = (e.target as HTMLElement).tagName;
            if (tag !== 'SELECT' && tag !== 'OPTION' && tag !== 'INPUT') e.preventDefault();
          }}
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            left: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            zIndex: 60,
            padding: '8px',
            background: '#fff',
            border: '1px solid #e5e5e5',
            borderRadius: '10px',
            boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
            fontFamily: 'Helvetica, Arial, sans-serif',
            fontSize: '12px',
            fontWeight: 400,
            fontStyle: 'normal',
            lineHeight: 1.2,
            color: '#2C2C2C',
            textAlign: 'left',
            maxWidth: '92vw'
          }}>

            <span style={{ fontSize: '11px', color: '#6E6659' }}>
              Selectionnez des mots ou des lettres, puis choisissez un reglage.
            </span>
            <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', color: '#7F1734', fontWeight: 600, minWidth: '78px' }}>
                Selection
              </span>
              <select
              aria-label="Taille de la selection"
              value=""
              onChange={(e) => e.target.value && applyCss({ fontSize: `${e.target.value}px` })}
              className={toolbarSelectClass}>

                <option value="">Taille</option>
                {TEXT_SIZE_OPTIONS.map((size) =>
              <option key={size} value={size}>
                    {size} px
                  </option>
              )}
              </select>
              <select
              aria-label="Police de la selection"
              value=""
              onChange={(e) => {
                if (!e.target.value) return;
                ensureFontLoaded(e.target.value);
                applyCss({ fontFamily: getFontById(e.target.value).stack });
              }}
              className={toolbarSelectClass}>

                <option value="">Police</option>
                {FONT_OPTIONS.map((font) =>
              <option key={font.id} value={font.id}>
                    {font.label}
                  </option>
              )}
              </select>
              <select
              aria-label="Graisse de la selection"
              value=""
              onChange={(e) => e.target.value && applyCss({ fontWeight: e.target.value })}
              className={toolbarSelectClass}>

                <option value="">Graisse</option>
                {FONT_WEIGHT_OPTIONS.map((weight) =>
              <option key={weight.id} value={weight.value}>
                    {weight.label}
                  </option>
              )}
              </select>
              <button
              type="button"
              aria-label="Italique"
              onClick={() => toggleCommand('italic')}
              className="h-8 w-8 flex items-center justify-center rounded-md border border-gray-300 bg-white hover:bg-gray-50">

                <Italic size={14} />
              </button>
              <button
              type="button"
              aria-label="Souligne"
              onClick={() => toggleCommand('underline')}
              className="h-8 w-8 flex items-center justify-center rounded-md border border-gray-300 bg-white hover:bg-gray-50">

                <Underline size={14} />
              </button>
              <label
              title="Couleur de la selection"
              className="h-8 w-8 flex items-center justify-center rounded-md border border-gray-300 bg-white hover:bg-gray-50 cursor-pointer overflow-hidden">

                <input
                type="color"
                aria-label="Couleur de la selection"
                defaultValue="#7F1734"
                onChange={(e) => applyCss({ color: e.target.value })}
                style={{ width: '32px', height: '32px', padding: 0, border: 0, background: 'none', cursor: 'pointer' }} />

              </label>
              <button
              type="button"
              aria-label="Effacer la mise en forme de la selection"
              title="Effacer la mise en forme de la selection"
              onClick={clearFormat}
              className="h-8 w-8 flex items-center justify-center rounded-md border border-gray-300 bg-white hover:bg-gray-50">

                <Eraser size={14} />
              </button>
            </span>
            <BlockStyleBar value={editStyle} onChange={setEditStyle} />
          </span>
        }
        {/* Barre de style : taille, police, graisse, italique */}
        {textStyles && !richEnabled &&
        <span
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            left: 0,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '6px',
            zIndex: 60,
            padding: '6px',
            background: '#fff',
            border: '1px solid #e5e5e5',
            borderRadius: '10px',
            boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
            fontFamily: 'Helvetica, Arial, sans-serif',
            fontSize: '12px',
            fontWeight: 400,
            fontStyle: 'normal',
            lineHeight: 1.2,
            whiteSpace: 'nowrap'
          }}>

            <select
            aria-label="Taille du texte"
            value={editStyle.fontSize ? String(editStyle.fontSize) : ''}
            onChange={(e) => updateStyle({ fontSize: e.target.value ? Number(e.target.value) : undefined })}
            className={toolbarSelectClass}>

              <option value="">Taille auto</option>
              {TEXT_SIZE_OPTIONS.map((size) =>
            <option key={size} value={size}>
                  {size} px
                </option>
            )}
            </select>
            <select
            aria-label="Police"
            value={editStyle.fontFamily || ''}
            onChange={(e) => updateStyle({ fontFamily: e.target.value || undefined })}
            className={toolbarSelectClass}>

              <option value="">Police du site</option>
              {FONT_OPTIONS.map((font) =>
            <option key={font.id} value={font.id}>
                  {font.label}
                </option>
            )}
            </select>
            <select
            aria-label="Graisse"
            value={editStyle.fontWeight || ''}
            onChange={(e) => updateStyle({ fontWeight: e.target.value || undefined })}
            className={toolbarSelectClass}>

              <option value="">Graisse auto</option>
              {FONT_WEIGHT_OPTIONS.map((weight) =>
            <option key={weight.id} value={weight.id}>
                  {weight.label}
                </option>
            )}
            </select>
            <button
            type="button"
            aria-label="Italique"
            aria-pressed={!!editStyle.italic}
            onClick={() => updateStyle({ italic: !editStyle.italic })}
            className={`h-8 w-8 flex items-center justify-center rounded-md border transition-colors ${
            editStyle.italic ?
            'bg-[#7F1734] text-white border-[#7F1734]' :
            'bg-white text-[#2C2C2C] border-gray-300 hover:bg-gray-50'}`
            }>

              <Italic size={14} />
            </button>
            <button
            type="button"
            aria-label="Reinitialiser le style"
            title="Revenir au style par defaut"
            onClick={() => setEditStyle({})}
            className="h-8 w-8 flex items-center justify-center rounded-md border border-gray-300 bg-white text-[#2C2C2C] hover:bg-gray-50">

              <RotateCcw size={14} />
            </button>
          </span>
        }
        {/* Action buttons — rendered as a span to stay DOM-valid inside any parent tag */}
        <span
          style={{
            position: 'absolute',
            bottom: '-40px',
            right: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            zIndex: 50
          }}>

          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 bg-[#7F1734] text-white text-xs font-medium rounded-md shadow-lg hover:bg-[#6b1229] transition-colors disabled:opacity-50">

            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={handleCancel}
            disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 bg-white text-[#2C2C2C] text-xs font-medium rounded-md shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50">

            <X size={12} />
            Cancel
          </button>
        </span>
      </EditingWrapper>);

  }

  // View mode with edit hover indicator
  // Use inline-block wrapper for inline tags to keep overlays valid
  const viewStyle = isInlineTag ?
  { ...style, position: 'relative' as const, display: 'inline-block' as const } :
  { ...style, position: 'relative' as const };

  return (
    <Tag
      className={`${className} group/editable cursor-pointer`}
      style={{ ...viewStyle, ...styleCss }}
      onClick={() => setIsEditing(true)}
      {...styleAttrs}
      {...wrapperProps}>

      {content}
      {/* Hover border + edit icon — use span to stay DOM-valid */}
      <span
        style={{
          position: 'absolute',
          inset: 0,
          border: '2px dashed transparent',
          borderRadius: '8px',
          pointerEvents: 'none',
          transition: 'border-color 200ms'
        }}
        className="group-hover/editable:!border-[#7F1734]/40" />

      <span
        style={{
          position: 'absolute',
          top: '-12px',
          right: '-12px',
          opacity: 0,
          pointerEvents: 'none',
          zIndex: 30,
          transition: 'opacity 200ms'
        }}
        className="group-hover/editable:!opacity-100">

        <span
          style={{
            width: '28px',
            height: '28px',
            background: '#7F1734',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 6px rgba(0,0,0,0.15)'
          }}>

          <Pencil size={12} className="text-white" />
        </span>
      </span>
    </Tag>);

};

export default EditableText;